/**
 *
 * FeedbackPost
 *
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  getUSADateFromFullDate,
  getUSATimeFromFullDate,
} from '@utils/timeHelpers';
import Text, { TEXT_SIZE, TEXT_COLOR } from '@components/Text';
import SectionLoading from '@components/SectionLoading';
import EmojiPickerButton from '@components/EmojiPickerButton';
import {
  CancelIcon,
  EditIcon,
  SaveIcon,
} from '@shared/Resources/Skill/Builder/styles';
import { getIconsByType } from '@services/icons';
import iconTypes from '@services/icons/generalIconTypes';
import {
  FeedbackPostContainer,
  FeedbackPostHeader,
  PostAvatar,
  PostDetails,
  PostText,
  ActionButtonsWrapper,
  IconWrapper,
  EmojiPickerWrapper,
  StyledTextArea,
  MeaningfulFeedbackControl,
  MeaningfulFeedbackCheckbox,
} from './styles';
import { editFeedback, deleteFeedback } from '../../actions';
import { openPopUp } from '../../../../../App/containers/PopUp/actions';
import POPUP_TYPES from '../../../../../App/containers/PopUp/types';

const DeleteIcon = getIconsByType(iconTypes.DELETE);

const FeedbackPost = ({ post, postsLoading }) => {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMeaningful, setIsMeaningful] = useState(Boolean(post.payload.isMeaningful));
  const feedbackRef = useRef();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const userId = useSelector((state) =>
    state.getIn(['app', 'authentication', 'user', 'id']),
  );
  const canEdit = userId === post.publisherUser.id;

  useEffect(() => {
    if (editing) {
      feedbackRef.current.value = post.payload.text;
    }
  }, [editing]);

  useEffect(() => {
    if (postsLoading.includes(post.id)) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [postsLoading, post]);

  function handleEditFeedback() {
    if (loading) return;
    setIsMeaningful(Boolean(post.payload.isMeaningful));
    setEditing(true);
  }
  function handleCancelFeedback() {
    setIsMeaningful(Boolean(post.payload.isMeaningful));
    setEditing(false);
  }
  function handleSaveFeedback() {
    setEditing(false);
    dispatch(editFeedback(post.id, feedbackRef.current.value, isMeaningful));
  }

  function handleEmojiSelect(emoji) {
    if (!feedbackRef.current) return;

    const textAreaNode = feedbackRef.current;
    const currentValue = textAreaNode.value || '';
    const start = textAreaNode.selectionStart ?? currentValue.length;
    const end = textAreaNode.selectionEnd ?? currentValue.length;
    const nextValue = `${currentValue.slice(0, start)}${emoji}${currentValue.slice(end)}`;
    const nextCursorPosition = start + emoji.length;

    feedbackRef.current.value = nextValue;
    feedbackRef.current.focus();
    feedbackRef.current.setSelectionRange(nextCursorPosition, nextCursorPosition);
  }

  function handleDeleteFeedback() {
    if (loading) return;
    dispatch(openPopUp(POPUP_TYPES.DELETE_MESSAGE_CONFIRM, {
      onConfirm: () => dispatch(deleteFeedback(post.id)),
      message: 'Are you sure you want to delete this feedback?',
      hideCloseIcon: true,
    }));
  }

  return (
    <FeedbackPostContainer>
      <FeedbackPostHeader>
        <PostAvatar
          className="posterAvatar"
          src={post.publisherUser.avatar}
        />
        <PostDetails>
          <Text
            size={TEXT_SIZE.H4}
            textColor={TEXT_COLOR.DARK_BLUE}
            className="posterName"
          >
            {post.publisherUser.fullName}
          </Text>
          <Text
            size={TEXT_SIZE.H4}
            textColor={TEXT_COLOR.DISABLED_LIGHT}
            className="postedOn"
          >
            {`${getUSADateFromFullDate(
              +post.createdAt,
            )}  ${getUSATimeFromFullDate(+post.createdAt)}`}
          </Text>
        </PostDetails>
        {canEdit
          && (editing ? (
            <ActionButtonsWrapper>
              <EmojiPickerWrapper>
                <EmojiPickerButton
                  onSelect={handleEmojiSelect}
                  recentStorageKey='novotalk_recent_emojis_feedback_edit'
                />
              </EmojiPickerWrapper>
              <IconWrapper>
                <SaveIcon onClick={handleSaveFeedback} />
              </IconWrapper>
              <IconWrapper>
                <CancelIcon onClick={handleCancelFeedback} />
              </IconWrapper>
            </ActionButtonsWrapper>
          ) : (
            <ActionButtonsWrapper>
              <IconWrapper>
                <EditIcon onClick={handleEditFeedback} />
              </IconWrapper>
              <IconWrapper>
                <DeleteIcon onClick={handleDeleteFeedback} />
              </IconWrapper>
            </ActionButtonsWrapper>
          ))}
      </FeedbackPostHeader>
      <PostText>
        {loading ? (
          <SectionLoading size={50} />
        ) : editing ? (
          <StyledTextArea ref={feedbackRef} />
        ) : (
          <Text
            size={TEXT_SIZE.H4}
            textColor={TEXT_COLOR.DARK_BLUE}
            className="postText"
          >
            {post.payload.text}
          </Text>
        )}
        {!loading && (
          <MeaningfulFeedbackControl
            control={(
              <MeaningfulFeedbackCheckbox
                checked={isMeaningful}
                disabled={!editing}
                onChange={(event) => setIsMeaningful(event.target.checked)}
                size='small'
              />
            )}
            label={t('Common.UI.meaningfulFeedback')}
          />
        )}
      </PostText>
    </FeedbackPostContainer>
  );
};

FeedbackPost.propTypes = {
  post: PropTypes.shape({
    createdAt: PropTypes.string,
    publisherUser: PropTypes.shape({
      avatar: PropTypes.string,
      fullName: PropTypes.string,
    }),
    text: PropTypes.string,
    id: PropTypes.string,
    payload: PropTypes.shape({
      text: PropTypes.string,
      isMeaningful: PropTypes.bool,
    }),
  }),
  postsLoading: PropTypes.arrayOf(PropTypes.string),
};

FeedbackPost.defaultProps = {
  postsLoading: [],
};

export default FeedbackPost;
